import { useEffect, useId, useState } from 'react';
import type { OfferFieldKey } from '@brimo/shared';
import { Banner, Button } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { confirmOffer, discardOffer, type OfferReview, type ReviewField } from './api';
import {
  OfferFieldInput,
  buildConfirmBody,
  mandatoryFilled,
  serverFieldErrors,
  type OfferFormErrors,
  type OfferFormValues,
} from './ManualOfferForm';
import styles from './financier.module.css';

/**
 * Finanzierer 06 „Angebot prüfen": first page of the PDF (260 px), the read fields with „erkannt"/„unsicher" and their
 * hints, all editable; „Verwerfen" (`DELETE`) / „Bestätigen und übernehmen" (`confirm`). Only with a configured
 * extractor — locally every upload goes to the manual entry.
 */

/** The editable text of a read field: days as `YYYY-MM-DD` for the date input, everything else as displayed. */
function initialText(field: ReviewField): string {
  if (field.key === 'expiresOn' || field.key === 'availableFromOn') {
    return typeof field.input === 'string' ? field.input : '';
  }
  return field.value ?? '';
}

export function OfferReviewCard({
  financingId,
  review,
  onDone,
}: {
  financingId: string;
  review: OfferReview;
  onDone: (stageMoved: boolean) => void;
}) {
  const titleId = useId();
  const keys: OfferFieldKey[] = review.fields.map((field) => field.key);
  const [values, setValues] = useState<OfferFormValues>({});
  const [errors, setErrors] = useState<OfferFormErrors>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState<'confirm' | 'discard' | null>(null);

  useEffect(() => {
    const next: OfferFormValues = {};
    for (const field of review.fields) next[field.key] = initialText(field);
    setValues(next);
    setErrors({});
    setBanner(null);
  }, [review.offerId]);

  async function confirm() {
    if (pending) return;
    const result = buildConfirmBody(values, keys);
    if ('errors' in result) {
      setErrors(result.errors);
      return;
    }
    setPending('confirm');
    setBanner(null);
    try {
      const outcome = await confirmOffer(financingId, review.offerId, result.body);
      onDone(outcome.stageMoved);
    } catch (error) {
      const fields = serverFieldErrors(error);
      if (fields) setErrors(fields);
      else setBanner(errorMessage(error));
    } finally {
      setPending(null);
    }
  }

  async function discard() {
    if (pending) return;
    setPending('discard');
    setBanner(null);
    try {
      await discardOffer(financingId, review.offerId);
      onDone(false);
    } catch (error) {
      setBanner(errorMessage(error));
    } finally {
      setPending(null);
    }
  }

  return (
    <section className={styles.card} aria-labelledby={titleId}>
      <div className={styles.cardHead}>
        <div className={styles.cardHeadText}>
          <div id={titleId} className={styles.cardTitle}>
            Angebot prüfen
          </div>
          <div className={styles.cardSubtitle}>{review.fileLine}</div>
        </div>
      </div>
      {banner ? (
        <Banner tone="danger" style={{ marginTop: 14 }}>
          {banner}
        </Banner>
      ) : null}
      <div className={styles.reviewGrid}>
        <div className={styles.preview}>
          <object
            className={styles.previewObject}
            type="application/pdf"
            data={`/api/files/${review.fileId}#page=1&toolbar=0&navpanes=0`}
            aria-label="Seite 1 des Angebots (PDF-Vorschau)"
          >
            <div className={styles.previewFallback}>Seite 1 des Angebots (PDF-Vorschau)</div>
          </object>
        </div>
        <div className={styles.fieldsTwo}>
          {review.fields.map((field) => (
            <OfferFieldInput
              key={field.key}
              fieldKey={field.key}
              label={field.label}
              value={values[field.key] ?? ''}
              tag={field.tag}
              hint={field.hint}
              error={errors[field.key]}
              disabled={pending !== null}
              onChange={(value) => setValues((current) => ({ ...current, [field.key]: value }))}
            />
          ))}
        </div>
      </div>
      <div className={styles.cardFooter}>
        <span className={styles.footNote}>Angebote sieht nur das Team, nicht der Kunde.</span>
        <span className={styles.footActions}>
          <Button
            variant="secondary"
            loading={pending === 'discard'}
            disabled={pending === 'confirm'}
            onClick={() => void discard()}
          >
            Verwerfen
          </Button>
          <Button
            loading={pending === 'confirm'}
            disabled={pending === 'discard' || !mandatoryFilled(values)}
            onClick={() => void confirm()}
          >
            Bestätigen und übernehmen
          </Button>
        </span>
      </div>
    </section>
  );
}
